import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Scroller } from 'react-components';
import { v4 as uuid } from 'uuid';
import Link from '../Link';
import OverlayThumb from '../OverlayThumb';
import LevelIndicator from '../LevelIndicator';
import { getTrackEvent, getPracticeTrackEvent } from '../../lib/analytics';
import { getCurriculumImage } from '../../utils/curriculum-utils';
import { toTitleCase } from '../../utils/string-utils';

export const formatThumbUrl = (item, instrument, hasSubscription, isPracticeMode) => {
  const { 'activity-lesson-video': activityVideo, objectType, slug, type } = item || {};
  const lessonPrefix = hasSubscription ? 'lesson' : 'lessons';
  const coursePrefix = hasSubscription ? 'course' : 'courses';
  if (isPracticeMode) {
    return `/practice/${item.slug}`;
  }
  if (activityVideo || type === 'chord') {
    return `/${lessonPrefix}/${item.slug}`;
  }
  if (type === 'artist') {
    return `/${instrument}/artists?index=${slug}`;
  }
  if (objectType) {
    const prefix = type === 'lesson' ? lessonPrefix : coursePrefix;
    return `/${prefix}/${slug}`;
  }
  return `/${coursePrefix}/${slug}`;
};

export const getThumbUrl = (item, instrument = 'guitar', hasSubscription, isPracticeMode) => {
  const { url } = item || {};
  return url || formatThumbUrl(item, instrument, hasSubscription, isPracticeMode);
};

class ThumbList extends PureComponent {
  render() {
    const {
      containerLayout,
      fenderConnect,
      instrument,
      itemArr,
      isScrollerEnabled,
      itemClasses,
      itemStyle,
      alphaSort,
      sortBy,
      aspectRatio,
      showDescription,
      variantType,
    } = this.props;
    const itemAlignStyle = containerLayout === 'column' ? 'db' : 'dib v-top';
    const parsedItemStyles = `${itemAlignStyle} mb4 ${itemClasses}`;
    if (alphaSort) {
      if (sortBy === 'artist') {
        itemArr.sort((a, b) => {
          if (a.artists[0].name.toLowerCase() > b.artists[0].name.toLowerCase()) {
            return 1;
          }
          if (b.artists[0].name.toLowerCase() > a.artists[0].name.toLowerCase()) {
            return -1;
          }
          return 0;
        });
      } else if (itemArr[0] && itemArr[0].type === 'chord') {
        itemArr.sort((a, b) => {
          const titleA = a.overlayTitle || a.title;
          const titleB = b.overlayTitle || b.title;
          if (titleA.length > titleB.length) {
            return 1;
          }
          if (titleB.length > titleA.length) {
            return -1;
          }
          return 0;
        });
      } else {
        itemArr.sort((a, b) => {
          if (a.title.replace(/[^\w\s]/gi, '') > b.title.replace(/[^\w\s]/gi, '')) {
            return 1;
          }
          if (b.title.replace(/[^\w\s]/gi, '') > a.title.replace(/[^\w\s]/gi, '')) {
            return -1;
          }
          return 0;
        });
      }
    }
    return (
      <Scroller enableMaxWidthClass={false} isScrollerEnabled={isScrollerEnabled}>
        {Object.keys(itemArr).map(key => {
          const itemObj = itemArr[key];
          const {
            overlayColor,
            overlayTitle,
            title,
            trackEvent,
            type,
            description,
            'activity-lesson-video': activitylessonVideo,
          } = itemObj;
          const { artists } = activitylessonVideo || [];
          const { userID, hasSubscription } = fenderConnect || {};
          const isPracticeMode = itemObj['activity-type'] === 'practice-sheet';
          const trackObj = isPracticeMode
            ? getPracticeTrackEvent('Practice Mode Launched', itemObj, window.location.href)
            : trackEvent || getTrackEvent(itemObj, userID);
          let practiceModeDescription = toTitleCase(type);
          if (isPracticeMode && artists) {
            practiceModeDescription = artists[0].name;
          }
          return (
            <div key={uuid()} className={parsedItemStyles} style={itemStyle}>
              <Link
                to={getThumbUrl(itemObj, instrument, hasSubscription, isPracticeMode) || '#'}
                trackEvent={trackObj}
              >
                <OverlayThumb
                  aspectRatio={aspectRatio}
                  backgroundColor={overlayColor}
                  circle={type === 'chord' || type === 'circle' || type === 'artist'}
                  image={getCurriculumImage(itemObj)}
                  label={overlayTitle}
                  overlay={type === 'chord' || type === 'circle' || type === 'artist'}
                  variantType={variantType}
                />
                {title ? (
                  <h4
                    className={`source-sans-b pt2 pb1 ma0 f6 black ${
                      itemObj.type === 'artist' ? 'tc' : null
                    }`}
                  >
                    {title}
                  </h4>
                ) : null}
                {showDescription && description ? (
                  <div className="ttn mid-gray">{description}</div>
                ) : null}
                {isPracticeMode ? (
                  <div className="ttn mid-gray">{practiceModeDescription}</div>
                ) : null}
                {!isPracticeMode && itemObj.artists ? (
                  <div className={`source-sans mid-gray f6 ${itemObj.level ? 'mb1' : ''}`}>
                    {itemObj.artists.map(artist => (
                      <div key={uuid()}>{artist.name}</div>
                    ))}
                  </div>
                ) : null}
                {!isPracticeMode && itemObj.level ? (
                  <LevelIndicator
                    currentLevel={parseInt(itemObj.level, 10)}
                    levelFontSize="f5"
                    completeColor={this.props.pathColor}
                  />
                ) : null}
              </Link>
            </div>
          );
        })}
      </Scroller>
    );
  }
}

ThumbList.propTypes = {
  itemArr: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        image: PropTypes.string,
        title: PropTypes.string,
        subTitle: PropTypes.string,
        overlayTitle: PropTypes.string,
        overlayColor: PropTypes.string,
        url: PropTypes.string,
        level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        type: PropTypes.string,
      }),
    ).isRequired,
    PropTypes.shape({
      image: PropTypes.string,
      title: PropTypes.string,
      subTitle: PropTypes.string,
      overlayTitle: PropTypes.string,
      overlayColor: PropTypes.string,
      url: PropTypes.string,
      level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
    }).isRequired,
  ]).isRequired,
  containerLayout: PropTypes.string,
  fenderConnect: PropTypes.shape({
    hasSubscription: PropTypes.bool,
    userID: PropTypes.string,
  }),
  instrument: PropTypes.string,
  itemClasses: PropTypes.string,
  itemStyle: PropTypes.shape({}),
  pathColor: PropTypes.string,
  alphaSort: PropTypes.bool,
  sortBy: PropTypes.string,
  aspectRatio: PropTypes.string,
  variantType: PropTypes.string,
  isScrollerEnabled: PropTypes.bool,
  showDescription: PropTypes.bool,
};

ThumbList.defaultProps = {
  alphaSort: false,
  containerLayout: '', // column or row
  instrument: 'guitar',
  itemClasses: 'mr3',
  itemStyle: { width: '10rem', minWidth: '10rem' },
  aspectRatio: 'square',
  fenderConnect: {
    email: null,
    hasSubscription: false,
    userID: null,
  },
  variantType: null,
  isScrollerEnabled: true,
  pathColor: 'blue',
  showDescription: false,
  sortBy: 'az',
};

export default ThumbList;
